import React from "react";
import { RouteObject, useRoutes, } from "react-router";
import loadable from "@loadable/component";
import { allLocales } from "@shared/locales";
import { AccommodationType } from "@shared/models/article";
import { ProtectedRoute } from "./bundles/client-only-ui";

const Home = loadable(() => import(/* webpackChunkName: "page-home" */ "./pages/home/home"));
const SearchArticles = loadable(() => import(/* webpackChunkName: "page-search-articles" */ "./pages/search-articles/search-articles"));
const Contact = loadable(() => import(/* webpackChunkName: "page-contact" */ "./pages/contact/contact"));
const Article = loadable(() => import(/* webpackChunkName: "page-article" */ "./pages/article/article"));
const Checkout = loadable(() => import(/* webpackChunkName: "page-checkout" */ "./pages/cart/cart"));
const CartItems = loadable(() => import(/* webpackChunkName: "page-checkout-items" */ "./pages/cart/cart-items"));
const CartAccount = loadable(() => import(/* webpackChunkName: "page-checkout-account" */ "./pages/cart/cart-connexion"));
const CartInformation = loadable(() => import(/* webpackChunkName: "page-checkout-information" */ "./pages/cart/cart-information"));
const CartPayment = loadable(() => import(/* webpackChunkName: "page-checkout-payment" */ "./pages/cart/cart-payment"));
const CartPaymentCallback = loadable(() => import(/* webpackChunkName: "page-checkout-callback" */ "./pages/cart/payment-callback"));
const PrivacyPolicy = loadable(() => import(/* webpackChunkName: "page-privacy-policy" */ "./pages/privacy-policy/privacy-policy"));
const LegalMention = loadable(() => import(/* webpackChunkName: "page-legal-mention" */ "./pages/legal-mention/legal-mention"));
const NotFound = loadable(() => import(/* webpackChunkName: "page-not-found" */ "./pages/notFound/notFound"));
const CmsTemplate = loadable(() => import(/* webpackChunkName: "page-cms-template" */ "./pages/cms-template/cms-template"));
const FiscalSituation = loadable(() => import(/* webpackChunkName: "page-fiscal-situation" */ "./pages/fiscal-situation/fiscal-situation"));
const Profile = loadable(() => import(/* webpackChunkName: "page-profile" */ "./pages/me/profile"));
const SingleNew = loadable(() => import(/* webpackChunkName: "page-news" */ "./pages/news/single-new"));
const NewsOutlet = loadable(() => import(/* webpackChunkName: "page-news" */ "./pages/news/news-outlet"));
const NewsList = loadable(() => import(/* webpackChunkName: "page-news" */ "./pages/news/news-list"));

export const searchRoutes = (withChildren: boolean = true): RouteObject[] => [
  {
    path: "location/vacances",
    children: [
      {
        path: "",
        index: true,
        element: <SearchArticles type={AccommodationType.RENTAL} />
      },
      ...(!withChildren ? [] : [
        {
          path: ":id",
          element: <Article type={AccommodationType.RENTAL} />
        }
      ])
    ]
  },
  {
    path: "location/saison",
    children: [
      {
        path: "",
        index: true,
        element: <SearchArticles type={AccommodationType.SEASONAL} />
      },
      ...(!withChildren ? [] : [
        {
          path: ":id",
          element: <Article type={AccommodationType.SEASONAL} />
        }
      ])
    ]
  },
  {
    path: "location/annee",
    children: [
      {
        path: "",
        index: true,
        element: <SearchArticles type={AccommodationType.ANNUAL} />
      },
      ...(!withChildren ? [] : [
        {
          path: ":id",
          element: <Article type={AccommodationType.ANNUAL} />
        }
      ])
    ]
  },
  {
    path: "vente",
    children: [
      {
        path: "",
        index: true,
        element: <SearchArticles type={AccommodationType.SALE} />
      },
      ...(!withChildren ? [] : [
        {
          path: ":id",
          element: <Article type={AccommodationType.SALE} />
        }
      ])
    ]
  }
];

const routes: RouteObject[] = [
  {
    path: "",
    element: <Home />,
    index: true,
  },
  ...searchRoutes(),
  {
    path: "services/:slug",
    element: <CmsTemplate rubric="services" />
  },
  {
    path: "agency/:slug",
    element: <CmsTemplate rubric="agency" />
  },
  {
    path: "situation-fiscale-en-valais",
    element: <FiscalSituation />
  },
  {
    path: "contact",
    element: <Contact />,
  },
  {
    path: "mentions-legales",
    element: <LegalMention />
  },
  {
    path: "politique-de-confidentialite",
    element: <PrivacyPolicy />
  },
  {
    path: "checkout",
    element: <Checkout />,
    children: [
      {
        path: "panier",
        element: <CartItems />
      },
      {
        path: "connexion",
        element: <CartAccount />
      },
      {
        path: "information",
        element: <CartInformation />
      },
      {
        path: "paiement",
        children: [
          {
            path: "",
            index: true,
            element: <CartPayment />
          },
          {
            path: "valide",
            element: <CartPaymentCallback succeeded={true} />
          },
          {
            path: "invalide",
            element: <CartPaymentCallback succeeded={false} />
          }
        ]
      }
    ]
  },
  {
    path: "me",
    children: [
      {
        path: "profile",
        element: <ProtectedRoute><Profile/></ProtectedRoute>
      }
    ]
  },
  {
    path: "actualites",
    element: <NewsOutlet/>,
    children: [
      {
        index: true,
        element: <NewsList />
      },
      {
        path: ":slug",
        element: <SingleNew/>
      }
    ]
  },
  {
    path: "*",
    element: <NotFound />
  }
];

const LocaleRoutes = () => useRoutes(allLocales.map(l => ({
  path: l.basename,
  children: routes
} as unknown as RouteObject)));

export default LocaleRoutes;
